import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

const InputContainer = styled(Box)({
  '--ri-font-family': 'inherit',
  '--ri-font-size': 'inherit',
  '--ri-font-weight': 'inherit',
  '--ri-line-height': 'inherit',
  '--ri-letter-spacing': 'inherit',

  display: 'inline-grid',

  '&::after, input': {
    width: 'auto',
    minWidth: '1em',
    gridArea: '1 / 2',
    paddingLeft: '2px',
    alignSelf: 'center',
  },

  '& input': {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },

  '&::after': {
    content: 'attr(data-value)', // uses the data-value attribute to create a hidden copy of the input text & automatically sizes to the input field to match its content.
    visibility: 'hidden',
    whiteSpace: 'nowrap',
    paddingRight: '1rem',
    fontFamily: 'var(--ri-font-family)',
    fontSize: 'var(--ri-font-size)',
    fontWeight: 'var(--ri-font-weight)',
    lineHeight: 'var(--ri-line-height)',
    letterSpacing: 'var(--ri-letter-spacing)',
  },
});

/**
 * Text input that grows horizontally to fit its content, with built-in
 * truncation handling.
 *
 * How it works:
 * - The input lives in an `inline-grid` container that also renders a hidden
 *   `::after` pseudo-element mirroring the input text via `attr(data-value)`.
 *   Both share the same grid cell, so the container's intrinsic width tracks
 *   the rendered text — making the input auto-size to its content.
 * - On mount, the input's computed font styles (family, size, weight,
 *   line-height, letter-spacing) are copied to CSS variables on the container
 *   so the `::after` mirror measures identically to the input.
 * - When an ancestor caps the width, the input stops growing and the text
 *   visually overflows. CSS `text-overflow: ellipsis` clips it with an
 *   ellipsis (visible when blurred — browsers scroll instead while focused).
 * - A `ResizeObserver` plus per-keystroke check compares `scrollWidth` to
 *   `clientWidth`; when the input is truncated, a MUI Tooltip reveals the
 *   full value on hover. No tooltip renders otherwise.
 *
 * Usage: drop-in for `<input>` — accepts all standard input HTML attributes
 * and forwards the ref to the underlying `<input>` element.
 */
export const ResizableInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [liveValue, setLiveValue] = useState(() => String(props.value ?? props.defaultValue ?? ''));
  const [isTruncated, setIsTruncated] = useState(false);

  const setInputRef = useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref]
  );

  const sync = useCallback(() => {
    const input = inputRef.current;
    const container = containerRef.current;
    if (!input || !container) return;
    container.dataset.value = input.value || input.placeholder || '';
    setLiveValue(input.value);
    setIsTruncated(input.scrollWidth > input.clientWidth);
  }, []);

  useEffect(() => {
    sync();
  }, [sync, props.value, props.defaultValue]);

  useEffect(() => {
    const container = containerRef.current;
    const input = inputRef.current;
    if (!container || !input) return;
    const s = window.getComputedStyle(input);
    container.style.setProperty('--ri-font-family', s.fontFamily);
    container.style.setProperty('--ri-font-size', s.fontSize);
    container.style.setProperty('--ri-font-weight', s.fontWeight);
    container.style.setProperty('--ri-line-height', s.lineHeight);
    container.style.setProperty('--ri-letter-spacing', s.letterSpacing);

    const observer = new ResizeObserver(() => setIsTruncated(input.scrollWidth > input.clientWidth));
    observer.observe(input);
    return () => observer.disconnect();
  }, []);

  return (
    <Tooltip title={isTruncated ? liveValue : ''} placement="bottom">
      <InputContainer ref={containerRef}>
        <input
          size={1}
          {...props}
          ref={setInputRef}
          onInput={(e) => {
            sync();
            props.onInput?.(e);
          }}
        />
      </InputContainer>
    </Tooltip>
  );
});

ResizableInput.displayName = 'ResizableInput';
